import type { Channel } from '../../../utils';
import type { Solution } from '../../../xstate/prostudio.xstate.types';

// A `type`, not an `interface`: `<Select data>` takes `SearchData`, an index
// signature, which an interface is never assignable to.
export type CredentialModalOption = {
  /** The `<SelectOption>` value — also the `id` the Select's typeahead matches on. */
  id: string;
  label: string;
};

/**
 * Solutions the user can scope the Channel list to (PSTUDIO-3829).
 *
 * Keyed by the solution `id`. This used to be `botSolutionId`, but Agent Manager
 * v2 creates no bot, so that field is null on every new solution and the list
 * came up empty. A channel records the bot that starts its chat (`Channel.botId`);
 * the modal maps the picked solution to every id its streams may carry with
 * `solutionStartChatIds` and passes them to {@link getChannelOptions}.
 *
 * Sorted by label because the list can run to 100+ entries on some projects, and
 * the Select's own typeahead re-sorts alphabetically once a search is cleared —
 * so an unsorted initial order would visibly reshuffle on first use.
 */
export const getSolutionOptions = (
  solutions?: Solution[]
): CredentialModalOption[] =>
  (solutions ?? [])
    .filter((solution) => !!solution.id)
    .map((solution) => {
      const id = solution.id as string;
      return { id, label: solution.displayName || solution.name || id };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Channels of one solution the user can scope a credential to (PSTUDIO-2990).
 *
 * The option's value is the pype id — a credential IS pype-scoped — while the
 * label is the channel name, because "pype" reads as an opaque id to the user and
 * repeats for every channel behind the same pype.
 *
 * Deduped by pype id: a pype can back several channels, and two options carrying
 * the same value would be an ambiguous choice. When that happens the names are
 * joined so the option still says what it covers.
 *
 * `solutionStartChatIds` is every id the picked solution's streams may carry:
 * `<customerName>.<uuid>` for any channel created since the v2 migration, plus the
 * legacy `botSolutionId` a pre-migration channel still has.
 *
 * Returns nothing without a solution (PSTUDIO-3829). The Channel select is
 * disabled in that state, so an empty list is never what the user is looking at —
 * this replaced an implicit scope taken from the breadcrumb, which listed every
 * project channel whenever the page had been opened by a direct link.
 */
export const getChannelOptions = (
  channels: Channel[],
  solutionStartChatIds: readonly string[]
): CredentialModalOption[] => {
  const ids = new Set(solutionStartChatIds);
  if (!ids.size) return [];

  const byPype = new Map<string, string[]>();
  for (const channel of channels) {
    if (!channel.pypeId || !channel.botId || !ids.has(channel.botId)) continue;

    const names = byPype.get(channel.pypeId);
    if (names) {
      if (!names.includes(channel.name)) names.push(channel.name);
    } else {
      byPype.set(channel.pypeId, [channel.name]);
    }
  }

  return [...byPype.entries()].map(([id, names]) => ({
    id,
    label: names.filter(Boolean).join(', ') || id,
  }));
};
